import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CreditCard, ArrowLeft, Save, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSettings = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('stripe');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [platformFee, setPlatformFee] = useState(20);
  
  // Show/hide password fields
  const [showStripeSecret, setShowStripeSecret] = useState(false);
  const [showStripeWebhook, setShowStripeWebhook] = useState(false);
  const [showPayPalSecret, setShowPayPalSecret] = useState(false);
  
  // Stripe settings
  const [stripeSettings, setStripeSettings] = useState({
    enabled: false,
    mode: 'sandbox', // 'sandbox' or 'live'
    publishable_key_sandbox: '',
    secret_key_sandbox: '',
    webhook_secret_sandbox: '',
    publishable_key_live: '',
    secret_key_live: '',
    webhook_secret_live: '',
    checkout_type: 'hosted' // 'hosted' or 'embedded'
  });
  
  // PayPal settings
  const [paypalSettings, setPaypalSettings] = useState({
    enabled: false,
    mode: 'sandbox', // 'sandbox' or 'live'
    product_type: 'standard', // 'standard', 'rest', 'subscriptions'
    client_id_sandbox: '',
    client_secret_sandbox: '',
    client_id_live: '',
    client_secret_live: '',
    payment_type: 'both' // 'onetime', 'recurring', 'both'
  });

  useEffect(() => {
    if (!user || !user.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchSettings();
  }, [user, navigate]);

  const fetchSettings = async () => {
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.get(`${API}/admin/payment-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.stripe) {
        setStripeSettings(response.data.stripe);
      }
      if (response.data.paypal) {
        setPaypalSettings(response.data.paypal);
      }
      
      // Fetch platform fee
      const feeResponse = await axios.get(`${API}/payment-settings/platform-fee`);
      setPlatformFee(feeResponse.data.fee_percentage);
    } catch (error) {
      console.error('Error fetching payment settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStripe = async (e) => {
    e.preventDefault();
    setSaving(true);
    const token = localStorage.getItem('session_token');
    
    try {
      await axios.post(`${API}/admin/payment-settings/stripe`, stripeSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Stripe settings saved successfully!');
      fetchSettings();
    } catch (error) {
      console.error('Error saving Stripe settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save Stripe settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePayPal = async (e) => {
    e.preventDefault();
    setSaving(true);
    const token = localStorage.getItem('session_token');
    
    try {
      await axios.post(`${API}/admin/payment-settings/paypal`, paypalSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('PayPal settings saved successfully!');
      fetchSettings();
    } catch (error) {
      console.error('Error saving PayPal settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save PayPal settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePlatformFee = async (e) => {
    e.preventDefault();
    setSaving(true);
    const token = localStorage.getItem('session_token');
    
    try {
      await axios.post(`${API}/admin/payment-settings/platform-fee`, 
        { fee_percentage: parseFloat(platformFee) },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Platform fee updated successfully!');
    } catch (error) {
      toast.error('Failed to update platform fee');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 flex items-center justify-center">
          <div className="text-white text-xl">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center mb-8">
            <Button
              onClick={() => navigate('/admin')}
              variant="ghost"
              className="text-white hover:bg-white/10 mr-4"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-3xl sm:text-4xl font-bold text-white">Payment Settings</h1>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            <Button
              onClick={() => setActiveTab('stripe')}
              variant={activeTab === 'stripe' ? 'default' : 'outline'}
              className={activeTab === 'stripe' ? 'bg-blue-600 hover:bg-blue-700' : 'border-white/20 text-white hover:bg-white/10'}
            >
              Stripe
            </Button>
            <Button
              onClick={() => setActiveTab('paypal')}
              variant={activeTab === 'paypal' ? 'default' : 'outline'}
              className={activeTab === 'paypal' ? 'bg-blue-600 hover:bg-blue-700' : 'border-white/20 text-white hover:bg-white/10'}
            >
              PayPal
            </Button>
            <Button
              onClick={() => setActiveTab('platformfee')}
              variant={activeTab === 'platformfee' ? 'default' : 'outline'}
              className={activeTab === 'platformfee' ? 'bg-blue-600 hover:bg-blue-700' : 'border-white/20 text-white hover:bg-white/10'}
            >
              Platform Fee
            </Button>
          </div>

          {/* Stripe Settings */}
          {activeTab === 'stripe' && (
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <CreditCard className="w-6 h-6 mr-2 text-blue-400" />
                  Stripe Configuration
                </CardTitle>
                <p className="text-gray-400 text-sm">
                  Configure Stripe payment gateway for subscriptions and one-time payments
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveStripe} className="space-y-6">
                  {/* Enable/Disable */}
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="stripe-enabled"
                      checked={stripeSettings.enabled}
                      onChange={(e) => setStripeSettings({ ...stripeSettings, enabled: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="stripe-enabled" className="text-white cursor-pointer">
                      Enable Stripe Payments
                    </Label>
                  </div>

                  {/* Mode Selection */}
                  <div>
                    <Label className="text-gray-300">Mode</Label>
                    <Select
                      value={stripeSettings.mode}
                      onValueChange={(value) => setStripeSettings({ ...stripeSettings, mode: value })}
                    >
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sandbox">Sandbox (Test Mode)</SelectItem>
                        <SelectItem value="live">Live (Production)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Checkout Type */}
                  <div>
                    <Label className="text-gray-300">Checkout Type</Label>
                    <Select
                      value={stripeSettings.checkout_type}
                      onValueChange={(value) => setStripeSettings({ ...stripeSettings, checkout_type: value })}
                    >
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hosted">Hosted Checkout (Stripe-hosted page)</SelectItem>
                        <SelectItem value="embedded">Embedded Form (Custom integration)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="border-t border-white/10 pt-6">
                    <h3 className="text-white font-semibold mb-4">
                      {stripeSettings.mode === 'sandbox' ? 'Sandbox (Test) Keys' : 'Live (Production) Keys'}
                    </h3>
                    
                    {/* Publishable Key */}
                    <div className="mb-4">
                      <Label className="text-gray-300">Publishable Key</Label>
                      <Input
                        type="text"
                        value={stripeSettings.mode === 'sandbox' ? stripeSettings.publishable_key_sandbox : stripeSettings.publishable_key_live}
                        onChange={(e) => {
                          if (stripeSettings.mode === 'sandbox') {
                            setStripeSettings({ ...stripeSettings, publishable_key_sandbox: e.target.value });
                          } else {
                            setStripeSettings({ ...stripeSettings, publishable_key_live: e.target.value });
                          }
                        }}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="pk_test_... or pk_live_..."
                      />
                      <p className="text-xs text-gray-500 mt-1">Get from Stripe Dashboard → Developers → API keys</p>
                    </div>

                    {/* Secret Key */}
                    <div className="mb-4">
                      <Label className="text-gray-300">Secret Key</Label>
                      <div className="relative">
                        <Input
                          type={showStripeSecret ? "text" : "password"}
                          value={stripeSettings.mode === 'sandbox' ? stripeSettings.secret_key_sandbox : stripeSettings.secret_key_live}
                          onChange={(e) => {
                            if (stripeSettings.mode === 'sandbox') {
                              setStripeSettings({ ...stripeSettings, secret_key_sandbox: e.target.value });
                            } else {
                              setStripeSettings({ ...stripeSettings, secret_key_live: e.target.value });
                            }
                          }}
                          className="bg-white/5 border-white/10 text-white pr-10"
                          placeholder="sk_test_... or sk_live_..."
                        />
                        <button
                          type="button"
                          onClick={() => setShowStripeSecret(!showStripeSecret)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                        >
                          {showStripeSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Keep this secure! Get from Stripe Dashboard → Developers → API keys</p>
                    </div>

                    {/* Webhook Secret */}
                    <div className="mb-4">
                      <Label className="text-gray-300">Webhook Secret</Label>
                      <div className="relative">
                        <Input
                          type={showStripeWebhook ? "text" : "password"}
                          value={stripeSettings.mode === 'sandbox' ? stripeSettings.webhook_secret_sandbox : stripeSettings.webhook_secret_live}
                          onChange={(e) => {
                            if (stripeSettings.mode === 'sandbox') {
                              setStripeSettings({ ...stripeSettings, webhook_secret_sandbox: e.target.value });
                            } else {
                              setStripeSettings({ ...stripeSettings, webhook_secret_live: e.target.value });
                            }
                          }}
                          className="bg-white/5 border-white/10 text-white pr-10"
                          placeholder="whsec_..."
                        />
                        <button
                          type="button"
                          onClick={() => setShowStripeWebhook(!showStripeWebhook)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                        >
                          {showStripeWebhook ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Get from Stripe Dashboard → Developers → Webhooks</p>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700 w-full"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Stripe Settings'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* PayPal Settings */}
          {activeTab === 'paypal' && (
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <CreditCard className="w-6 h-6 mr-2 text-blue-600" />
                  PayPal Configuration
                </CardTitle>
                <p className="text-gray-400 text-sm">
                  Configure PayPal payment gateway for flexible payment options
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSavePayPal} className="space-y-6">
                  {/* Enable/Disable */}
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="paypal-enabled"
                      checked={paypalSettings.enabled}
                      onChange={(e) => setPaypalSettings({ ...paypalSettings, enabled: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="paypal-enabled" className="text-white cursor-pointer">
                      Enable PayPal Payments
                    </Label>
                  </div>

                  {/* Mode Selection */}
                  <div>
                    <Label className="text-gray-300">Mode</Label>
                    <Select
                      value={paypalSettings.mode}
                      onValueChange={(value) => setPaypalSettings({ ...paypalSettings, mode: value })}
                    >
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sandbox">Sandbox (Test Mode)</SelectItem>
                        <SelectItem value="live">Live (Production)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Product Type */}
                  <div>
                    <Label className="text-gray-300">PayPal Product</Label>
                    <Select
                      value={paypalSettings.product_type}
                      onValueChange={(value) => setPaypalSettings({ ...paypalSettings, product_type: value })}
                    >
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">PayPal Standard (Button-based)</SelectItem>
                        <SelectItem value="rest">PayPal REST API (Advanced)</SelectItem>
                        <SelectItem value="subscriptions">PayPal Subscriptions API</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">Choose based on your integration needs</p>
                  </div>

                  {/* Payment Type */}
                  <div>
                    <Label className="text-gray-300">Payment Type</Label>
                    <Select
                      value={paypalSettings.payment_type}
                      onValueChange={(value) => setPaypalSettings({ ...paypalSettings, payment_type: value })}
                    >
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="onetime">One-time Payments Only</SelectItem>
                        <SelectItem value="recurring">Recurring Subscriptions Only</SelectItem>
                        <SelectItem value="both">Both Payment Types</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="border-t border-white/10 pt-6">
                    <h3 className="text-white font-semibold mb-4">
                      {paypalSettings.mode === 'sandbox' ? 'Sandbox (Test) Keys' : 'Live (Production) Keys'}
                    </h3>
                    
                    {/* Client ID */}
                    <div className="mb-4">
                      <Label className="text-gray-300">Client ID</Label>
                      <Input
                        type="text"
                        value={paypalSettings.mode === 'sandbox' ? paypalSettings.client_id_sandbox : paypalSettings.client_id_live}
                        onChange={(e) => {
                          if (paypalSettings.mode === 'sandbox') {
                            setPaypalSettings({ ...paypalSettings, client_id_sandbox: e.target.value });
                          } else {
                            setPaypalSettings({ ...paypalSettings, client_id_live: e.target.value });
                          }
                        }}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="AXX..."
                      />
                      <p className="text-xs text-gray-500 mt-1">Get from PayPal Developer Dashboard → My Apps & Credentials</p>
                    </div>

                    {/* Client Secret */}
                    <div className="mb-4">
                      <Label className="text-gray-300">Client Secret</Label>
                      <div className="relative">
                        <Input
                          type={showPayPalSecret ? "text" : "password"}
                          value={paypalSettings.mode === 'sandbox' ? paypalSettings.client_secret_sandbox : paypalSettings.client_secret_live}
                          onChange={(e) => {
                            if (paypalSettings.mode === 'sandbox') {
                              setPaypalSettings({ ...paypalSettings, client_secret_sandbox: e.target.value });
                            } else {
                              setPaypalSettings({ ...paypalSettings, client_secret_live: e.target.value });
                            }
                          }}
                          className="bg-white/5 border-white/10 text-white pr-10"
                          placeholder="EXX..."
                        />
                        <button
                          type="button"
                          onClick={() => setShowPayPalSecret(!showPayPalSecret)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                        >
                          {showPayPalSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Keep this secure! Get from PayPal Developer Dashboard</p>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700 w-full"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save PayPal Settings'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Platform Fee Settings */}
          {activeTab === 'platformfee' && (
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Platform Fee Configuration</CardTitle>
                <p className="text-gray-400 text-sm">
                  Set the platform commission on tip sales and subscriptions
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSavePlatformFee} className="space-y-6">
                  <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4 mb-6">
                    <p className="text-yellow-400 text-sm">
                      <strong>Important:</strong> This fee applies to ALL tipster earnings from tip sales and monthly subscriptions. 
                      Tipsters will see this fee percentage in their profile and wallet.
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="platform-fee" className="text-white">Platform Fee Percentage (%)</Label>
                    <Input
                      id="platform-fee"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={platformFee}
                      onChange={(e) => setPlatformFee(e.target.value)}
                      className="bg-white/10 border-white/20 text-white"
                      required
                    />
                    <p className="text-gray-400 text-sm mt-2">
                      Default: 20% (similar to Udemy, Fiverr). Tipsters keep {100 - platformFee}% of each sale.
                    </p>
                  </div>

                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                    <h4 className="text-white font-semibold mb-2">Fee Breakdown Example</h4>
                    <p className="text-gray-300 text-sm">If a tip costs €10 with {platformFee}% fee:</p>
                    <div className="mt-2 space-y-1 text-sm">
                      <div className="flex justify-between text-gray-300">
                        <span>Sale Amount:</span>
                        <span className="text-white font-semibold">€10.00</span>
                      </div>
                      <div className="flex justify-between text-gray-300">
                        <span>Platform Fee ({platformFee}%):</span>
                        <span className="text-red-400">-€{(10 * platformFee / 100).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-white font-semibold border-t border-white/20 pt-1 mt-1">
                        <span>Tipster Earns:</span>
                        <span className="text-green-400">€{(10 * (100 - platformFee) / 100).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={saving}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {saving ? 'Saving...' : 'Update Platform Fee'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default PaymentSettings;
